/**
 * OfferCardsTabTray — local clone of TabTray for the DtvOfferCards filter tabs.
 *
 * This is a [[ NOT IN BIT ]] variant from Figma (file: SWrqfoP6T2GK8xYPt7mWkp).
 * All design tokens are extracted directly from Figma MCP get_design_context.
 *
 * Key differences from the shared TabTray:
 * - Exact Figma dimensions (40px tray / 36px tabs) vs shared defaults (52px / 48px)
 * - Mobile horizontal scroll with viewport bleed (Figma annotation)
 * - All tokens hardcoded — no guessing, no parent overrides needed
 */
import React, { useRef, useMemo } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import DtvTypography from "@directv-digital/dds.ui.dtv-typography/";
import { getAnalyticsAttrs } from "@directv-digital/dds.services.analytics-service";

export interface OfferCardsTabTrayProps {
  id?: string;
  tabs: { label: string }[];
  defaultSelectedTab?: number;
  ariaLabel?: string;
  handleChange?: (index: number) => void;
}

// All values below are from Figma MCP get_design_context — zero guessing.
// Figma file: SWrqfoP6T2GK8xYPt7mWkp
const FIGMA_TOKENS = {
  // Tray container (node 6817:48)
  trayBg: "rgba(16, 38, 65, 0.04)",           // --transparent-blue900_4pct
  trayBorderTop: "1px solid rgba(16, 38, 65, 0.12)", // --transparent-blue900_12pct
  trayPadding: "2px",                          // --spacing01
  trayBorderRadius: "9999px",                  // --shape-pill
  trayHeight: "40px",                          // metadata: frame height

  // Tabs container (node 6817:49)
  tabGap: "4px",                               // --spacing02

  // Tab items (nodes 6817:50, 6817:51)
  tabPadding: "7.5px 16px",                    // --spacing05 horizontal
  tabHeight: "36px",                           // metadata: instance height
  tabBorderRadius: "9999px",                   // --shape-pill

  // Selected tab (node 6817:50)
  selectedBg: "#102641",                       // --button-primary-bg-active
  selectedColor: "#FFFFFF",                    // --text-inverse-primary

  // Unselected tab (node 6817:51)
  unselectedColor: "#747474",                  // --neutral600

  // Hover state (from component docs)
  hoverBg: "rgba(16, 38, 65, 0.08)",

  // Focus state (from component docs)
  focusOutline: "2px solid #1FBCF8",

  // Active/press state
  activeBg: "rgba(16, 38, 65, 0.2)",

  // Typography (Font block from get_design_context)
  // Mobile/Body/Body 2 medium: Roboto Medium 16px/1.3
  // fontSize and fontWeight handled by DtvTypography variant="body2medium"
  lineHeight: "1.3"
};

export function OfferCardsTabTray({
  id,
  tabs,
  defaultSelectedTab = 0,
  ariaLabel,
  handleChange
}: OfferCardsTabTrayProps) {
  const [value, setValue] = React.useState(defaultSelectedTab);
  const tabsRef = useRef<HTMLDivElement>(null);

  const trayStyles = useMemo(() => ({
    // Tray container — Figma node 6817:48
    // Always the same size/shape. Bleeds naturally when viewport is too narrow.
    width: "fit-content",
    flexShrink: 0,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    backgroundColor: FIGMA_TOKENS.trayBg,
    border: "none",
    borderTop: FIGMA_TOKENS.trayBorderTop,
    borderRadius: FIGMA_TOKENS.trayBorderRadius,
    padding: FIGMA_TOKENS.trayPadding,
    height: FIGMA_TOKENS.trayHeight,
    minHeight: FIGMA_TOKENS.trayHeight,

    // MUI Tabs indicator = selected tab background
    "& .MuiTabs-indicator": {
      height: "100% !important",
      backgroundColor: FIGMA_TOKENS.selectedBg,
      borderRadius: FIGMA_TOKENS.tabBorderRadius,
      zIndex: 0
    },

    // Prevent MUI from clipping — parent handles scroll, not MUI scroller
    "& .MuiTabs-scroller": {
      overflow: "visible !important"
    },

    // Tabs flex container — Figma node 6817:49
    "& .MuiTabs-flexContainer": {
      gap: FIGMA_TOKENS.tabGap
    },

    // Individual tab items — Figma nodes 6817:50-55
    "& .MuiTab-root": {
      zIndex: 1,
      padding: FIGMA_TOKENS.tabPadding,
      textTransform: "none",
      width: "fit-content",
      minWidth: "auto",
      maxWidth: "none",
      minHeight: FIGMA_TOKENS.tabHeight,
      height: FIGMA_TOKENS.tabHeight,
      borderRadius: FIGMA_TOKENS.tabBorderRadius,
      color: FIGMA_TOKENS.unselectedColor,
      transition: "color 250ms ease",
      whiteSpace: "nowrap",
      "&.Mui-selected": {
        color: `${FIGMA_TOKENS.selectedColor} !important`,
        transition: "color 0.3s ease"
      },
      ":hover": {
        backgroundColor: FIGMA_TOKENS.hoverBg
      },
      "&.Mui-focusVisible": {
        outline: FIGMA_TOKENS.focusOutline,
        outlineOffset: "-2px"
      },
      ":active": {
        backgroundColor: FIGMA_TOKENS.activeBg
      }
    }
  }), []);

  const handleChangeTab = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    handleChange?.(newValue);

    // Scroll clipped tabs into view so the user sees the full label
    requestAnimationFrame(() => {
      const container = tabsRef.current;
      if (!container) {
        return;
      }
      const selectedTab = container.querySelector<HTMLElement>(".Mui-selected");
      selectedTab?.scrollIntoView({
        inline: "nearest",
        block: "nearest",
        behavior: "smooth"
      });
    });
  };

  return (
    <Tabs
      ref={tabsRef}
      sx={trayStyles}
      value={value}
      variant="standard"
      onChange={handleChangeTab}
    >
      {tabs.map((tab, index) => (
        <Tab
          disableRipple
          id={id ? `${id}-tab-${index}` : undefined}
          key={index}
          tabIndex={0}
          {...getAnalyticsAttrs(tab.label?.toLowerCase(), 'component_tab', typeof window !== 'undefined' ? window.location.href : '', `${id}-tab-${index}`)}
          aria-label={ariaLabel || `Toggle to ${tab.label}`}
          label={
            <DtvTypography
              variant="body2medium"
              sx={{
 lineHeight: FIGMA_TOKENS.lineHeight,
zIndex: 2
}}
            >
              {tab.label}
            </DtvTypography>
          }
        />
      ))}
    </Tabs>
  );
}
